import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { MOCK_PIPELINE } from '../data/mockSeed'
import { MiscLogo } from '../components/MiscLogo'
import { PortalShell } from '../components/PortalShell'
import { TopBar } from '../components/TopBar'
import { OnboardingThemeProvider } from '../theme/OnboardingThemeProvider'
import { MISC_PRIMARY } from '../theme/onboardingTheme'
import { AppRoutes } from '../../helper/appRoutes'

export function InternalDashboard() {
  const [toast, setToast] = useState<string | null>(null)

  return (
    <OnboardingThemeProvider>
      <PortalShell
        heroNarrow
        heroTitle="MISC internal — client onboarding pipeline"
        heroSubtitle="Prototype view for staff demo. All rows use fictional clients."
        showHeroLogo
      >
        <TopBar />
        <Box sx={{ display: { xs: 'flex', lg: 'none' }, flex: 1, flexDirection: 'column', px: 2, py: 3 }}>
          <MiscLogo />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowX: 'auto',
            px: { xs: 2, lg: 5 },
            pb: 6,
            pt: { xs: 1, lg: 3 },
          }}
        >
          <Box sx={{ mx: 'auto', maxWidth: '72rem' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a' }}>
                Onboarding pipeline
              </Typography>
              <Link
                component={RouterLink}
                to={AppRoutes.client.home}
                sx={{ fontSize: 14, fontWeight: 600, color: MISC_PRIMARY, '&:hover': { textDecoration: 'underline' } }}
              >
                ← Client portal home
              </Link>
            </Box>

            <Paper
              elevation={0}
              sx={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
                boxShadow: 1,
              }}
            >
              <Table sx={{ minWidth: 650, textAlign: 'left', fontSize: 14 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#f8fafc',
                      '& .MuiTableCell-head': {
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: '#64748b',
                        px: 2,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>Client</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell>Steps</TableCell>
                    <TableCell>Transition</TableCell>
                    <TableCell>Last activity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Flags</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {MOCK_PIPELINE.map((c) => (
                    <TableRow key={c.clientId} sx={{ borderTop: '1px solid #f1f5f9' }}>
                      <TableCell sx={{ px: 2, py: 1.5, fontWeight: 500, color: '#0f172a' }}>
                        {c.clientName}
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, fontSize: 12 }}>{c.serviceType}</TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, fontSize: 12 }}>
                        {c.stepsComplete}/{c.stepsTotal}
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, fontSize: 12 }}>{c.transitionDate ?? '—'}</TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, fontSize: 12, color: '#475569' }}>
                        {c.lastActivity}
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Box
                          component="span"
                          sx={{
                            borderRadius: 999,
                            px: 1,
                            py: 0.25,
                            fontSize: 11,
                            fontWeight: 600,
                            ...(c.status === 'Complete'
                              ? { backgroundColor: '#d1fae5', color: '#065f46' }
                              : c.status === 'Blocked'
                                ? { backgroundColor: '#fee2e2', color: '#991b1b' }
                                : { backgroundColor: '#fef3c7', color: '#78350f' }),
                          }}
                        >
                          {c.status}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5, fontSize: 12 }}>
                        {c.overdue ? (
                          <Box component="span" sx={{ fontWeight: 600, color: '#dc2626' }}>
                            Overdue
                          </Box>
                        ) : null}
                        {c.anomaly ? (
                          <Box component="span" sx={{ display: 'block', color: '#92400e' }}>
                            {c.anomaly}
                          </Box>
                        ) : null}
                        {!c.overdue && !c.anomaly ? (
                          <Box component="span" sx={{ color: '#94a3b8' }}>
                            —
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1.5 }}>
                        <Button
                          type="button"
                          onClick={() => {
                            setToast(`Nudge queued for ${c.clientName} (demo).`)
                            window.setTimeout(() => setToast(null), 2500)
                          }}
                          sx={{
                            minWidth: 0,
                            p: 0,
                            fontSize: 12,
                            fontWeight: 600,
                            color: MISC_PRIMARY,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                          }}
                        >
                          Send nudge
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        </Box>
        {toast ? (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              zIndex: 50,
              transform: 'translateX(-50%)',
              borderRadius: '8px',
              backgroundColor: '#0f172a',
              px: 2,
              py: 1,
              fontSize: 14,
              color: '#ffffff',
              boxShadow: 6,
            }}
          >
            {toast}
          </Box>
        ) : null}
      </PortalShell>
    </OnboardingThemeProvider>
  )
}
